/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { App, Modal } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import React, { useEffect, useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { relationshipIdFromLabel } from '@sportninja/common/features/profile/family/utils'
import { applyFamilyApiError } from '@sportninja/common/features/profile/family/applyFamilyApiError'
import {
  FAMILY_SUCCESS_MESSAGES,
  SELF_RELATIONSHIP_TYPE_ID,
} from '@sportninja/common/features/profile/family/constants'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import WarningModal from 'src/components/WarningModal'
import { useMePlayerProfile } from '../../player/setupModal/hooks/useMePlayerProfile'
import { useUpdateFamilyContact } from '../../hooks/useUpdateFamilyContact'
import {
  toFamilyContactPlayer,
  type FamilyContactPlayerSource,
} from './toFamilyContactPlayer'
import type { FamilyScope } from '@sportninja/common/services/player/familyScope'

import FamilyContactForm, {
  type FamilyContactFormValues,
} from './FamilyContactForm'

const EMPTY_FORM: FamilyContactFormValues = {
  nameFirst: '',
  nameLast: '',
  email: '',
  phone: '',
  relationship: null,
  isPrimary: false,
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  // Sits ABOVE the Family Management modal (10001/10000) it opens on top of, so
  // that modal is masked behind this one instead of bleeding through.
  wrapper: {
    zIndex: 10003,
  },
  mask: {
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10002,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

export interface EditContactMember {
  id: string
  nameFirst: string
  nameLast: string
  /** Saved relationship_type id (preferred for prefill). */
  relationshipId?: number | null
  relationshipLabel?: string | null
  email?: string | null
  phone?: string | null
  /** Whether the contact has administrative access to the player. */
  isPrimary?: boolean
  /** Once accepted, identity fields lock — only relationship + admin editable. */
  isAccepted?: boolean
}

interface Props {
  isOpen: boolean
  onClose: () => void
  playerId: string | null
  /** Which backend routes to use. Omitted → self-scoped ('me'). */
  scope?: FamilyScope
  /**
   * Player for the card, when the caller already has it (the team roster
   * does). Supplying it skips the self-scoped player fetch, which the
   * roster's admin caller has no access to anyway.
   */
  player?: FamilyContactPlayerSource | null
  member: EditContactMember | null
}

const memberToFormValues = (
  member: EditContactMember
): FamilyContactFormValues => ({
  nameFirst: member.nameFirst ?? '',
  nameLast: member.nameLast ?? '',
  email: member.email ?? '',
  phone: member.phone ?? '',
  // Prefer the saved id; fall back to mapping the label to an id.
  relationship:
    member.relationshipId ?? relationshipIdFromLabel(member.relationshipLabel),
  isPrimary: Boolean(member.isPrimary),
})

const EditContactModal: React.FC<Props> = ({
  isOpen,
  onClose,
  playerId,
  member,
  scope,
  player,
}) => {
  const { message } = App.useApp()
  const updateMutation = useUpdateFamilyContact()
  // Only fetch when the caller did not supply the player — see the `player` prop.
  const { data: playerData } = useMePlayerProfile(player ? null : playerId)
  const cardPlayer = toFamilyContactPlayer(player ?? playerData)
  const [values, setValues] = useState<FamilyContactFormValues>(EMPTY_FORM)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  // Gate the save behind a warning when the user picks "Mark as Me" (Self) —
  // that reclassifies the contact as the player, dropping them from the list.
  const [showSelfWarning, setShowSelfWarning] = useState(false)

  // Prefill or reset whenever member changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setValues(member ? memberToFormValues(member) : EMPTY_FORM)
      setFieldErrors({})
      setShowSelfWarning(false)
    }
  }, [isOpen, member])

  const submitUpdate = async () => {
    if (!playerId || !member) {
      return
    }
    try {
      await updateMutation.mutateAsync({
        playerId,
        scope,
        contactId: member.id,
        name_first: values.nameFirst.trim(),
        name_last: values.nameLast.trim(),
        email: values.email.trim(),
        phone_number: values.phone || null,
        relationship_type_id: values.relationship,
        is_primary: values.isPrimary,
      })
      message.success(formatSuccessToast(FAMILY_SUCCESS_MESSAGES.edit))
      onClose()
    } catch (err) {
      applyFamilyApiError(err, { setFieldErrors, notifyError: message.error })
    }
  }

  const handleConfirm = async () => {
    // "Mark as Me" (Self) removes the member from the family list — confirm the
    // consequence before saving. Every other relationship saves immediately.
    if (values.relationship === SELF_RELATIONSHIP_TYPE_ID) {
      setShowSelfWarning(true)
      return
    }
    await submitUpdate()
  }

  const handleConfirmSelfWarning = async () => {
    setShowSelfWarning(false)
    await submitUpdate()
  }

  const handleFormChange = (next: FamilyContactFormValues) => {
    setFieldErrors((prev) => {
      if (Object.keys(prev).length === 0) {
        return prev
      }
      const cleared = { ...prev }
      ;(Object.keys(next) as (keyof FamilyContactFormValues)[]).forEach((k) => {
        if (next[k] !== values[k] && k in cleared) {
          delete cleared[k as string]
        }
      })
      return cleared
    })
    setValues(next)
  }

  const closeIcon = (
    <CloseOutlined
      css={css`
        color: ${colors.SOLITUDE};
        font-size: 16px;
      `}
    />
  )

  const header = (
    <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
      {capitalizeWords('Edit Family Member')}
    </Text>
  )

  const footer = (
    <div css={footerStyle}>
      <Button
        text='Cancel'
        onClick={onClose}
        variant='secondary'
        disabled={updateMutation.isPending}
      />
      <Button
        text='Confirm'
        onClick={handleConfirm}
        variant='primary'
        isLoading={updateMutation.isPending}
        disabled={updateMutation.isPending}
      />
    </div>
  )

  if (!isOpen) {
    return null
  }

  return (
    <>
      <Modal
        css={css`
          display: flex;
          flex: 1;
          max-height: 90vh;
          margin: 24px auto;
          padding: 0;

          .ant-modal-content {
            max-height: 90vh;
            border-radius: 8px;
            display: flex;
            flex-direction: column;
          }
          .ant-modal-body {
            flex: 1;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
          }
        `}
        width={600}
        closeIcon={closeIcon}
        title={header}
        footer={footer}
        open={isOpen}
        onCancel={onClose}
        centered
        styles={modalStyles}
        maskClosable={false}
      >
        <FamilyContactForm
          values={values}
          onChange={handleFormChange}
          player={cardPlayer}
          lockBaseFields={Boolean(member?.isAccepted)}
          fieldErrors={fieldErrors}
          allowSelfRelationship
        />
      </Modal>

      <WarningModal
        isOpen={showSelfWarning}
        onClose={() => setShowSelfWarning(false)}
        onConfirm={handleConfirmSelfWarning}
        title={capitalizeWords('Mark As Me')}
        description="By marking this member as yourself, they'll be removed from the family list. This action can't be undone here."
        cancelLabel='CANCEL'
        confirmLabel='CONFIRM'
        loadingLabel='SAVING…'
        isLoading={updateMutation.isPending}
      />
    </>
  )
}

export default EditContactModal

// ─── Styles ────────────────────────────────────────────────────────────────

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  width: 50%;
  margin-left: auto;
`
